import { z } from "zod";
import { isPlottedWidget, isSingleFigureWidget } from "./widget-catalogue.js";
export const DASHBOARD_MAX_COLUMNS = 24;
export const DASHBOARD_MAX_ROWS = 512;
/** The grid a dashboard is laid out on unless it names its own: twelve columns, and rows of 28px with 12px between them. */
export const DASHBOARD_DEFAULT_LAYOUT = { columns: 12, rowHeight: 28, gap: 12 };
/** The tallest a widget is, in rows: on the default grid, forty rows is 1,588px. */
export const DASHBOARD_MAX_WIDGET_HEIGHT = 40;
export const dashboardLayoutSchema = z.object({
    columns: z.number().int().min(1).max(DASHBOARD_MAX_COLUMNS).default(DASHBOARD_DEFAULT_LAYOUT.columns),
    rowHeight: z.number().int().min(24).max(320).default(DASHBOARD_DEFAULT_LAYOUT.rowHeight),
    gap: z.number().int().min(0).max(32).default(DASHBOARD_DEFAULT_LAYOUT.gap),
});
export const dashboardWidgetPositionSchema = z.object({
    x: z.number().int().min(0).max(DASHBOARD_MAX_COLUMNS - 1),
    y: z.number().int().min(0).max(DASHBOARD_MAX_ROWS - 1),
    w: z.number().int().min(1).max(DASHBOARD_MAX_COLUMNS),
    h: z.number().int().min(1).max(DASHBOARD_MAX_WIDGET_HEIGHT),
});
/** The least height a widget type reads in, in pixels: a figure and its change; a plot, its scale and its key; a list's first rows. */
function leastWidgetHeight(type) {
    if (isSingleFigureWidget(type))
        return 104;
    if (isPlottedWidget(type) || type === "record_list")
        return 216;
    return 152;
}
/** The fewest rows a widget of this type reads in, on a grid of this row height and gap. */
export function leastWidgetRows(type, layout) {
    return Math.max(1, Math.ceil((leastWidgetHeight(type) + layout.gap) / (layout.rowHeight + layout.gap)));
}
/**
 * A dashboard's widgets with none fewer rows than its type reads in on the grid's rows. A widget saved shorter (sized
 * for another grid's rows, or written by hand) is made that much taller, and as many rows are added under it across
 * the grid, so whatever was below moves down by them and the rows stay lined up. Widgets already tall enough keep
 * their size, and where all of them are the widgets are returned as they are.
 */
export function withLeastRows(widgets, layout) {
    // The rows added at each line a short widget ends on: as many as the widget ending there grows by the most.
    const added = new Map();
    for (const { type, position } of widgets) {
        const growth = leastWidgetRows(type, layout) - position.h;
        const end = position.y + position.h;
        if (growth > 0)
            added.set(end, Math.max(added.get(end) ?? 0, growth));
    }
    if (added.size === 0)
        return widgets;
    const addedAbove = (y) => [...added].reduce((rows, [line, count]) => (line <= y ? rows + count : rows), 0);
    return widgets.map((widget) => ({
        ...widget,
        position: {
            ...widget.position,
            y: widget.position.y + addedAbove(widget.position.y),
            h: Math.max(widget.position.h, leastWidgetRows(widget.type, layout)),
        },
    }));
}
export function dashboardLayoutIssues(layout, widgets) {
    const issues = [];
    const idIndexes = new Map();
    for (const [index, widget] of widgets.entries()) {
        const previousIndex = idIndexes.get(widget.id);
        if (previousIndex !== undefined) {
            issues.push({
                path: ["widgets", index, "id"],
                message: `Widget id "${widget.id}" duplicates widgets.${previousIndex}.id.`,
            });
        }
        else {
            idIndexes.set(widget.id, index);
        }
        if (widget.position.x + widget.position.w > layout.columns) {
            issues.push({
                path: ["widgets", index, "position", "w"],
                message: `Widget extends beyond the ${layout.columns}-column dashboard grid.`,
            });
        }
        if (widget.position.y + widget.position.h > DASHBOARD_MAX_ROWS) {
            issues.push({
                path: ["widgets", index, "position", "h"],
                message: `Widget extends beyond the ${DASHBOARD_MAX_ROWS}-row dashboard limit.`,
            });
        }
    }
    for (let leftIndex = 0; leftIndex < widgets.length; leftIndex += 1) {
        for (let rightIndex = leftIndex + 1; rightIndex < widgets.length; rightIndex += 1) {
            const left = widgets[leftIndex];
            const right = widgets[rightIndex];
            if (!dashboardPositionsOverlap(left.position, right.position))
                continue;
            issues.push({
                path: ["widgets", rightIndex, "position"],
                message: `Widget overlaps widgets.${leftIndex} (${left.id}).`,
            });
        }
    }
    return issues;
}
export function dashboardPositionsOverlap(left, right) {
    return left.x < right.x + right.w
        && left.x + left.w > right.x
        && left.y < right.y + right.h
        && left.y + left.h > right.y;
}
/**
 * Deterministically bounds and vertically compacts a dashboard. When pinnedId
 * is provided, that widget keeps its requested position and the remaining
 * widgets compact around it using stable y/x/id ordering.
 */
export function compactDashboardWidgets(widgets, columns, pinnedId) {
    const safeColumns = clamp(columns, 1, DASHBOARD_MAX_COLUMNS);
    const bounded = widgets.map((widget) => ({
        ...widget,
        position: boundDashboardPosition(widget.position, safeColumns),
    }));
    const pinned = pinnedId
        ? bounded.find((widget) => widget.id === pinnedId)
        : undefined;
    const rest = bounded
        .filter((widget) => widget.id !== pinned?.id)
        .sort(compareDashboardWidgets);
    const placed = [];
    if (pinned)
        placed.push(pinned);
    for (const widget of rest) {
        const candidate = { ...widget, position: { ...widget.position, y: 0 } };
        while (placed.some((other) => dashboardPositionsOverlap(candidate.position, other.position))) {
            candidate.position.y += 1;
        }
        if (candidate.position.y + candidate.position.h > DASHBOARD_MAX_ROWS) {
            throw new Error(`Dashboard layout exceeds the ${DASHBOARD_MAX_ROWS}-row limit.`);
        }
        placed.push(candidate);
    }
    const byId = new Map(placed.map((widget) => [widget.id, widget]));
    return widgets.map((widget) => byId.get(widget.id) ?? widget);
}
export function boundDashboardPosition(position, columns) {
    const safeColumns = clamp(columns, 1, DASHBOARD_MAX_COLUMNS);
    const w = clamp(position.w, 1, safeColumns);
    const h = clamp(position.h, 1, DASHBOARD_MAX_WIDGET_HEIGHT);
    return {
        x: clamp(position.x, 0, safeColumns - w),
        y: clamp(position.y, 0, DASHBOARD_MAX_ROWS - h),
        w,
        h,
    };
}
function compareDashboardWidgets(left, right) {
    return left.position.y - right.position.y
        || left.position.x - right.position.x
        || left.id.localeCompare(right.id);
}
function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
}
